import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import * as _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { calculateEncodedUrlFromUrlName, calculateUrlFromUrlName } from '~src/settings/general-settings/components/sections/site-name-and-id/SiteNameAndIdBusinessLogic';
import { SiteSettingsErrorCode } from '~src/ts/helpers/ErrorCodes';

import { IError } from '../../../httpClient/serverApi/types/IError';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { AskDataModeFromString } from '../../../settings/types/AskDataMode';
import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { ISiteAdminSettings } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../../FeatureFlags';
import { deepClone } from '../../helpers/DeepClone';
import { L10n } from '../../L10n/L10n';
import { ToasterService, ToastType } from '../../services/ToasterService';
import { CreateSiteDialogBody, CreateSiteDialogBodyProps, CreateSiteDialogBodyState } from '../components/CreateSiteDialogBody';
import { defaultSiteSettingsForServerAdmin, defaultSiteSettingsForSiteAdmin, Sites } from './resources/Sites';

export class CreateSiteAction {
  private server: SessionManagementService;
  constructor() {
    this.server = getSessionManagementServiceInstance();
  }

  public execute(): Promise<any> {
    return Sites.instance.getCurrentSiteSettingsForServerAdmin().then((siteSettingsForServerAdmin: ISiteSettingsForServerAdmin) => {
      const modalInstance = openReactActionDialog<CreateSiteDialogBodyProps, CreateSiteDialogBodyState, any>({
        title:  Messages.newSiteDialogTitle(),
        bodyComponent: CreateSiteDialogBody,
        initialBodyProps: {
          prompt: Messages.addUsersImportAdGroupSelectGroupTitle()
        },
        initialDialogState: { siteName: '', siteId: '' , siteUrl: calculateUrlFromUrlName(''), showInvalidSiteUrlErrorMessage: false,
        showDuplicateSiteNameErrorMessage: false, showDuplicateSiteUrlErrorMessage: false},
        actionName: Messages.newSiteDialogSubmit(),
        dismissDialogOnActionFailed: false,
        action: (_bodyProps: CreateSiteDialogBodyProps, dialogState: CreateSiteDialogBodyState,
          updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateSiteDialogBodyState>>) => {
          const siteAdminSettingsParam = this.getSanitizedSiteAdminSettings();
          const siteSettingsForServerAdminParam = this.getSanitizedSiteSettingsForServerAdmin(siteSettingsForServerAdmin, dialogState.siteName, dialogState.siteId);
          const siteUrl = calculateEncodedUrlFromUrlName(dialogState.siteId)();
          return Sites.instance.createSite(
            siteSettingsForServerAdminParam.settings,
            siteAdminSettingsParam,
            siteSettingsForServerAdminParam.subscriptionSettings)
            .then(() => {
              ToasterService.instance.pop({
                type: ToastType.Success,
                text: Messages.siteCreateSuccess({
                  SITE_NAME: L10n.elideIfLong(dialogState.siteName)
                }),
                actionHref: siteUrl,
                actionMessage: Messages.goToSite({
                  SITE_NAME: L10n.elideIfLong(dialogState.siteName)
                }),
                action: () => {window.open(siteUrl, '_self');}
              });
            }).catch((errors: IError[]) => {
              this.errorMessages(errors, siteSettingsForServerAdminParam,
                updateDialogState);
              return Promise.reject({});
            });
        },
        actionDisabled: (_bodyProps: CreateSiteDialogBodyProps, dialogState: CreateSiteDialogBodyState) => !dialogState.siteName || !dialogState.siteId || dialogState.showInvalidSiteUrlErrorMessage
          || dialogState.showDuplicateSiteNameErrorMessage || dialogState.showDuplicateSiteUrlErrorMessage,
        dialogWidth: TabDialogWidth.width375px,
      });
      return Promise.resolve(modalInstance.result);
    });
  }

  private errorMessages(errors: IError[], site: ISiteSettingsForServerAdmin,
    updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateSiteDialogBodyState>>): void {
    _.each(errors, (error: IError) => {
      if (error.code === SiteSettingsErrorCode.SiteNameAlreadyExists) {
        updateDialogState({showDuplicateSiteNameErrorMessage: true});
      } else if (error.code === SiteSettingsErrorCode.SiteUrlNameAlreadyExists) {
        updateDialogState({showDuplicateSiteUrlErrorMessage: true});
      } else {
        ToasterService.instance.pop({ type: ToastType.Error, text: Sites.instance.errorMessage(error.code, site) });
      }
    });
  }

  private getSanitizedSiteAdminSettings(): ISiteAdminSettings {
    let siteAdminSettingsParam: ISiteAdminSettings = deepClone(defaultSiteSettingsForSiteAdmin).siteAdminSettings as ISiteAdminSettings;

    (siteAdminSettingsParam as ISiteAdminSettings).askDataMode = AskDataModeFromString(this.server.defaultAskDataMode());

    // Default to derived permissions enabled if cataloging is enabled on the server.
    if (this.server.catalogingOnServerEnabled()) {
      siteAdminSettingsParam.derivedPermissionsEnabled = true;
    }
    return siteAdminSettingsParam;
  }

  private getSanitizedSiteSettingsForServerAdmin(siteSettingsForServerAdmin: ISiteSettingsForServerAdmin, siteName: string, urlName: string): ISiteSettingsForServerAdmin {
    let siteSettingsForServerAdminSanitized: ISiteSettingsForServerAdmin = deepClone(defaultSiteSettingsForServerAdmin);

    if (!_.isUndefined(siteSettingsForServerAdmin.subscriptionSettings)) {
      siteSettingsForServerAdminSanitized.subscriptionSettings.serverAllowSubscriptionAttachments =
      siteSettingsForServerAdmin.subscriptionSettings.serverAllowSubscriptionAttachments;
    }
    siteSettingsForServerAdminSanitized.settings.guestAccessEnabled = ServerSettingsUnauthenticated.isGuestEnabled();
    siteSettingsForServerAdminSanitized.settings.runNowEnabled = FeatureFlags.enableRunNowEnabled ? this.server.isRunNowEnabled() : undefined;
    siteSettingsForServerAdminSanitized.settings.customSubscriptionEmail =
      deepClone(this.server.defaultSubscriptionsEmail());


    siteSettingsForServerAdminSanitized.settings.name = siteName;
    siteSettingsForServerAdminSanitized.settings.urlName = urlName;
    return siteSettingsForServerAdminSanitized;
  }

}
